import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import Navbar from "../components/Navbar";
import ContactVendorleadsModal from "../components/ContactVendorleadsModal";

const BRAND_COLOR = "#1a3f6b";

function Legal() {
  const [showContact, setShowContact] = useState(false);
  const location = useLocation();

  // Scroll to the right anchor when arriving via /privacy-policy etc.
  useEffect(() => {
    const hash = (location.hash || "").replace("#", "");
    const target =
      hash ||
      (location.pathname === "/privacy-policy" ? "privacy" : "terms");
    const el = document.getElementById(target);
    if (el) {
      // Slight delay so layout settles first.
      setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    }
  }, [location.pathname, location.hash]);

  const openContact = (e) => {
    e.preventDefault();
    setShowContact(true);
  };

  return (
    <>
      <Navbar />
      <div
        style={{
          maxWidth: 800,
          margin: "0 auto",
          padding: "60px 24px 80px",
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
          color: "#333",
          lineHeight: 1.7,
        }}
      >
        <h1 style={{ color: BRAND_COLOR, fontSize: 32, marginBottom: 8 }}>
          Terms &amp; Privacy
        </h1>
        <p style={{ color: "#888", fontSize: 14, marginBottom: 24 }}>
          Last updated: 8 May 2026
        </p>

        <nav
          style={{
            display: "flex",
            gap: 16,
            marginBottom: 40,
            paddingBottom: 16,
            borderBottom: "1px solid #eee",
            fontSize: 14,
          }}
        >
          <a href="#terms" style={{ color: BRAND_COLOR, fontWeight: 600 }}>
            Terms and Conditions
          </a>
          <a href="#privacy" style={{ color: BRAND_COLOR, fontWeight: 600 }}>
            Privacy Policy
          </a>
        </nav>

        {/* ------------------------------------------------------------------ */}
        {/* Terms and Conditions                                                */}
        {/* ------------------------------------------------------------------ */}
        <h2
          id="terms"
          style={{
            color: BRAND_COLOR,
            fontSize: 26,
            marginTop: 8,
            marginBottom: 24,
            scrollMarginTop: 80,
          }}
        >
          Terms and Conditions
        </h2>

        <Section title="1. Provision of Services">
          <p>
            Vendorleads.com.au agrees to provide the services described in the
            applicable Order Form ("Services") in a professional and timely
            manner using reasonable care and skill.
          </p>
          <p>
            Services may include data ingestion, lead tracking, reporting,
            automated communications, analytics, and related software
            functionality as specified in the Order Form.
          </p>
        </Section>

        <Section title="2. Support Services">
          <p>
            Vendorleads.com.au will provide reasonable technical and operational
            support during the term of this Agreement, including onboarding
            assistance and general platform support.
          </p>
        </Section>

        <Section title="3. Customer Data Ownership">
          <SubHeading>3.1 Ownership</SubHeading>
          <p>
            The Customer retains full ownership of all data, records, contacts,
            and information extracted from the Customer's systems or otherwise
            provided in connection with the Services ("Customer Data").
          </p>
          <p>
            Vendorleads.com.au acquires no ownership rights in Customer Data.
          </p>

          <SubHeading>3.2 No Marketing Use</SubHeading>
          <p>
            Vendorleads.com.au will not use Customer Data for its own marketing
            or promotional purposes.
          </p>
          <p>
            Customer Data will not be sold, shared, or disclosed to third
            parties except:
          </p>
          <ul>
            <li>As necessary to deliver the Services</li>
            <li>As required by law</li>
            <li>With the Customer's prior written consent</li>
          </ul>
        </Section>

        <Section title="4. Use of Customer Data">
          <p>
            Vendorleads.com.au may access and process Customer Data solely for
            the purpose of delivering and improving the Services under this
            Agreement.
          </p>
          <p>
            Any aggregated or anonymised data used for service improvement will
            not identify the Customer or any individual.
          </p>
        </Section>

        <Section title="5. Data Storage and Location">
          <p>
            Customer Data is stored in Australia using a Sydney-region Snowflake
            data warehouse instance.
          </p>
          <p>
            Vendorleads.com.au will not transfer Customer Data outside Australia
            unless required for essential infrastructure services and subject to
            appropriate data protection safeguards.
          </p>
        </Section>

        <Section title="6. Privacy Compliance">
          <p>Vendorleads.com.au agrees to comply with:</p>
          <ul>
            <li>The Privacy Act 1988 (Cth)</li>
            <li>The Australian Privacy Principles (APPs)</li>
            <li>All other applicable Australian privacy and data protection laws</li>
          </ul>
          <p>
            Vendorleads.com.au will process personal information only in
            accordance with this Agreement and applicable law. See the{" "}
            <a href="#privacy" style={{ color: BRAND_COLOR }}>
              Privacy Policy
            </a>{" "}
            below for full details.
          </p>
        </Section>

        <Section title="7. Security Standards">
          <p>
            Vendorleads.com.au maintains commercially reasonable security
            safeguards consistent with modern software engineering best
            practices, including:
          </p>
          <ul>
            <li>Encryption of data in transit (TLS)</li>
            <li>Encryption of data at rest</li>
            <li>Role-based access controls</li>
            <li>Restricted internal access to Customer Data</li>
            <li>Secure authentication mechanisms</li>
          </ul>
          <p>Security practices are reviewed periodically.</p>
        </Section>

        <Section title="8. Data Breach Notification">
          <p>
            If Vendorleads.com.au becomes aware of a confirmed or suspected data
            breach involving Customer Data, it will:
          </p>
          <ul>
            <li>Notify the Customer in writing within 24 hours</li>
            <li>Provide relevant details regarding the breach</li>
            <li>Take immediate steps to contain and remediate the issue</li>
            <li>
              Cooperate with the Customer in meeting any legal notification
              obligations
            </li>
          </ul>
        </Section>

        <Section title="9. Spam Act Compliance">
          <p>
            Vendorleads.com.au confirms that its systems are designed to support
            compliance with the Spam Act 2003 (Cth), including:
          </p>
          <ul>
            <li>Functional unsubscribe mechanisms</li>
            <li>Clear sender identification</li>
            <li>Consent-based communication workflows</li>
          </ul>
          <p>
            The Customer remains responsible for ensuring that uploaded contact
            lists have been lawfully obtained and that appropriate consent
            exists where required.
          </p>
        </Section>

        <Section title="10. Intellectual Property">
          <SubHeading>10.1 Vendor Intellectual Property</SubHeading>
          <p>
            Vendorleads.com.au retains all rights, title, and interest in its
            software, code, tools, systems, methodologies, processes, and
            improvements ("Vendor IP").
          </p>
          <p>
            No ownership rights in Vendor IP are transferred to the Customer.
          </p>

          <SubHeading>10.2 Licence</SubHeading>
          <p>
            Subject to payment of applicable fees, Vendorleads.com.au grants the
            Customer a non-exclusive, non-transferable licence to access and use
            the Services during the term of this Agreement.
          </p>
        </Section>

        <Section title="11. Fees and Payment">
          <p>
            Fees are billed monthly in advance unless otherwise stated in the
            Order Form.
          </p>
          <p>
            Failure to pay may result in suspension of Services after reasonable
            notice.
          </p>
          <p>
            No long-term lock-in applies unless expressly stated in the Order
            Form.
          </p>
        </Section>

        <Section title="12. Term and Termination">
          <SubHeading>12.1 Term</SubHeading>
          <p>
            This Agreement commences on the Effective Date and continues until
            terminated.
          </p>

          <SubHeading>12.2 Termination for Convenience</SubHeading>
          <p>
            Either party may terminate this Agreement at any time by providing
            written notice.
          </p>

          <SubHeading>12.3 Immediate Termination</SubHeading>
          <p>
            The Customer may terminate immediately if Vendorleads.com.au
            materially breaches this Agreement and fails to remedy the breach
            within a reasonable period after notice.
          </p>

          <SubHeading>12.4 No Future Liability</SubHeading>
          <p>Upon termination:</p>
          <ul>
            <li>The Customer is liable only for fees accrued up to the termination date</li>
            <li>No future fees apply</li>
            <li>No early termination penalties apply unless expressly stated in the Order Form</li>
          </ul>
        </Section>

        <Section title="13. Data Deletion Upon Termination">
          <p>
            Upon termination, Vendorleads.com.au will permanently delete
            Customer Data from active systems within a reasonable timeframe.
          </p>
          <p>Written confirmation of deletion will be provided upon request.</p>
          <p>
            Backup data will be removed in accordance with standard system
            retention cycles unless required by law.
          </p>
        </Section>

        <Section title="14. Confidentiality">
          <p>
            Each party agrees to keep confidential any non-public information
            received from the other party and to use such information solely for
            fulfilling obligations under this Agreement.
          </p>
          <p>Confidentiality obligations survive termination.</p>
        </Section>

        {/* ------------------------------------------------------------------ */}
        {/* Privacy Policy                                                      */}
        {/* ------------------------------------------------------------------ */}
        <h2
          id="privacy"
          style={{
            color: BRAND_COLOR,
            fontSize: 26,
            marginTop: 56,
            marginBottom: 24,
            paddingTop: 24,
            borderTop: "2px solid #eee",
            scrollMarginTop: 80,
          }}
        >
          Privacy Policy
        </h2>

        <Section title="1. Introduction">
          <p>
            Vendorleads ("we", "us", or "our") operates the Vendorleads platform
            (the "Service"). This Privacy Policy explains how we collect, use,
            disclose, and safeguard your information when you use our Service,
            including any information provided through third-party integrations
            such as Facebook.
          </p>
          <p>
            By using our Service you agree to the collection and use of
            information in accordance with this policy.
          </p>
        </Section>

        <Section title="2. Information We Collect">
          <p>We may collect the following categories of information:</p>
          <ul>
            <li>
              <strong>Account information</strong> — name, email address, phone
              number, agency details, and login credentials.
            </li>
            <li>
              <strong>Profile and settings</strong> — agency branding, logo,
              social media links, and communication preferences.
            </li>
            <li>
              <strong>Third-party platform data</strong> — when you connect a
              Facebook Page or other social media account, we receive an access
              token, your Page name, and Page ID. We do <em>not</em> access your
              personal Facebook profile, friends list, or private messages.
            </li>
            <li>
              <strong>Usage data</strong> — pages visited, features used, clicks,
              and device/browser information collected automatically via cookies
              and similar technologies.
            </li>
            <li>
              <strong>Content you create</strong> — articles, social media blurbs,
              email campaigns, property reports, and other materials generated
              through the platform.
            </li>
          </ul>
        </Section>

        <Section title="3. How We Use Your Information">
          <ul>
            <li>To provide, maintain, and improve the Service.</li>
            <li>
              To publish content on your behalf to connected social media accounts
              (e.g. posting to your Facebook Page) only when you explicitly
              initiate or schedule a post.
            </li>
            <li>To send transactional emails, notifications, and support communications.</li>
            <li>To personalise your experience and generate AI-powered content.</li>
            <li>To monitor usage patterns and improve performance and security.</li>
            <li>To comply with legal obligations.</li>
          </ul>
        </Section>

        <Section title="4. Facebook Data Use">
          <p>
            When you connect your Facebook account we request only the permissions
            necessary to post on your Page:
          </p>
          <ul>
            <li>
              <strong>pages_manage_posts</strong> — to publish posts on your
              connected Facebook Page.
            </li>
            <li>
              <strong>pages_read_engagement</strong> — to read basic Page
              information.
            </li>
            <li>
              <strong>pages_show_list</strong> — to display the list of Pages you
              manage so you can choose which to connect.
            </li>
          </ul>
          <p>
            We store an encrypted Page access token solely for the purpose of
            publishing posts you create through our platform. We do not sell,
            share, or use your Facebook data for advertising, analytics resale, or
            any purpose unrelated to the Service.
          </p>
          <p>
            You may disconnect your Facebook Page at any time from the Settings
            page, which will immediately delete the stored token.
          </p>
        </Section>

        <Section title="5. Data Sharing and Disclosure">
          <p>We do not sell your personal information. We may share data with:</p>
          <ul>
            <li>
              <strong>Service providers</strong> — hosting (AWS), email delivery,
              AI model providers (OpenAI), and payment processors, solely to
              operate the Service.
            </li>
            <li>
              <strong>Legal requirements</strong> — if required by law, regulation,
              or legal process.
            </li>
            <li>
              <strong>Business transfers</strong> — in connection with a merger,
              acquisition, or sale of assets, with notice to you.
            </li>
          </ul>
        </Section>

        <Section title="6. Data Retention">
          <p>
            We retain your information for as long as your account is active or as
            needed to provide the Service. You may request deletion of your
            account and associated data at any time by contacting us.
          </p>
        </Section>

        <Section title="7. Data Security">
          <p>
            We implement industry-standard security measures including encryption
            at rest and in transit, access controls, and regular security reviews.
            However, no method of transmission over the Internet is 100% secure,
            and we cannot guarantee absolute security.
          </p>
        </Section>

        <Section title="8. Your Rights">
          <p>Depending on your jurisdiction, you may have the right to:</p>
          <ul>
            <li>Access, correct, or delete your personal data.</li>
            <li>Withdraw consent for data processing.</li>
            <li>Export your data in a portable format.</li>
            <li>Object to or restrict certain processing activities.</li>
          </ul>
          <p>
            To exercise these rights,{" "}
            <a
              href="#"
              onClick={openContact}
              style={{ color: BRAND_COLOR, cursor: "pointer" }}
            >
              contact us
            </a>
            .
          </p>
        </Section>

        <Section title="9. Cookies">
          <p>
            We use essential cookies to keep you logged in and functional cookies
            to remember your preferences. We may also use analytics cookies to
            understand usage patterns. You can control cookie settings through your
            browser.
          </p>
        </Section>

        <Section title="10. Children's Privacy">
          <p>
            Our Service is not directed to individuals under 18. We do not
            knowingly collect personal information from children.
          </p>
        </Section>

        <Section title="11. Changes to This Policy">
          <p>
            We may update these Terms and this Privacy Policy from time to time.
            We will notify you of material changes by posting the updated policy
            on this page with a revised "Last updated" date. Continued use of the
            Service after changes constitutes acceptance.
          </p>
        </Section>

        <Section title="12. Contact Us">
          <p>
            If you have questions about these Terms or this Privacy Policy, please
            contact us:
          </p>
          <ul style={{ listStyle: "none", paddingLeft: 0 }}>
            <li>
              <a
                href="#"
                onClick={openContact}
                style={{ color: BRAND_COLOR, cursor: "pointer" }}
              >
                Contact Form
              </a>
            </li>
            <li>Website: vendorleads.com.au</li>
          </ul>
          <ContactVendorleadsModal
            show={showContact}
            onHide={() => setShowContact(false)}
          />
        </Section>
      </div>
    </>
  );
}

function Section({ title, children }) {
  return (
    <section style={{ marginBottom: 32 }}>
      <h3
        style={{
          fontSize: 18,
          color: BRAND_COLOR,
          marginBottom: 12,
          borderBottom: "1px solid #eee",
          paddingBottom: 8,
        }}
      >
        {title}
      </h3>
      {children}
    </section>
  );
}

function SubHeading({ children }) {
  return (
    <h4
      style={{
        fontSize: 15,
        color: BRAND_COLOR,
        margin: "16px 0 6px",
        fontWeight: 600,
      }}
    >
      {children}
    </h4>
  );
}

export default Legal;
